import classNames from 'classnames';
import { ContentTitle } from '@components/content/ContentTitle';
import { Body } from '@components/text';
import './ContentMeta.scss';

export interface ContentMetaProps {
  /** Text align, default left */
  align?: 'left' | 'right' | 'center';
  /** Main title text */
  primary?: React.ReactNode;
  /** Smaller white text above title, e.g. episode */
  secondary?: React.ReactNode;
  /** Smaller grey text above title, e.g. date */
  tertiary?: React.ReactNode;
  /** Additional tertiary-styled text */
  extra?: React.ReactNode;
  /** Small or large, default large */
  size?: 'small' | 'large';
  /** Primary text line clamp */
  primaryLineClamp?: 1 | 2;
  /** Apply text truncation to the last top item (whichever of secondary, tertiary, or extra is rendered last) */
  hasTruncatedLastTopItem?: boolean;
  className?: string;
}

/**
 * ContentMeta component
 * @param align - text align, default left
 * @param primary - main title text
 * @param secondary - smaller white text above title, e.g. episode
 * @param tertiary - smaller grey text above title, e.g. date
 * @param extra - additional tertiary-styled text
 * @param size - small or large, default large
 * @param primaryLineClamp - primary text line clamp
 * @param hasTruncatedLastTopItem - apply text truncation to the last top item
 * @param className - optional style class
 */

export const ContentMeta: React.FC<ContentMetaProps> = ({
  align = 'left',
  primary,
  secondary,
  tertiary,
  extra,
  size = 'large',
  primaryLineClamp = 2,
  hasTruncatedLastTopItem,
  className,
}) => {
  const topTextSize = size === 'large' ? 'medium' : 'small';

  return (
    <div className={classNames('vds-content-meta', className)} style={{ textAlign: align }}>
      <div
        className={classNames('vds-content-meta__top', {
          'vds-content-meta__top--truncate': hasTruncatedLastTopItem,
        })}>
        {secondary && (
          <Body
            className="vds-content-meta__top-item"
            variant="caption"
            size={topTextSize}
            weight="semibold"
            color="primary">
            {secondary}
          </Body>
        )}
        {tertiary && (
          <Body
            className="vds-content-meta__top-item"
            variant="caption"
            size={topTextSize}
            weight="regular"
            color="tertiary">
            {tertiary}
          </Body>
        )}
        {extra && (
          <Body
            className="vds-content-meta__top-item"
            variant="caption"
            size={topTextSize}
            weight="regular"
            color="tertiary">
            {extra}
          </Body>
        )}
      </div>
      {primary && (
        <ContentTitle lineClamp={primaryLineClamp} align={align}>
          {primary}
        </ContentTitle>
      )}
    </div>
  );
};
